import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function SupportPage() {
  const [form, setForm] = useState({ name: "", email: "", user_type: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim() || !form.email.includes("@")) e.email = "Valid email is required";
    if (!form.user_type) e.user_type = "Please select your role";
    if (!form.subject.trim()) e.subject = "Subject is required";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Message must be at least 10 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/support", form);
      setSent(true);
      toast.success("Message sent successfully!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const set = (key, val) => { setForm({ ...form, [key]: val }); if (errors[key]) setErrors({ ...errors, [key]: "" }); };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="support-page">
      <header className="bg-white border-b border-[#E6E6E6] py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/"><Button variant="ghost" size="icon"><ArrowLeft size={18} /></Button></Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-white border border-[#E6E6E6] flex items-center justify-center p-0.5">
              <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>SportsUnity</span>
          </div>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 sm:px-6 py-10">
        {sent ? (
          <div className="text-center py-16" data-testid="support-success">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Outfit' }}>Message Sent!</h1>
            <p className="text-sm text-zinc-500 mb-6">We've received your message and will get back to you as soon as possible.</p>
            <div className="flex gap-3 justify-center">
              <Link to="/"><Button variant="outline" className="border-[#E6E6E6] text-[#074572]">Back to Home</Button></Link>
              <Button onClick={() => { setSent(false); setForm({ name: "", email: "", user_type: "", subject: "", message: "" }); }} className="bg-[#074572] text-white">Send Another</Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-medium text-zinc-900 mb-1" style={{ fontFamily: 'Outfit' }}>Support</h1>
            <p className="text-sm text-zinc-500 mb-8">Have a question or need help? Send us a message and we'll get back to you.</p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-zinc-700">Full Name <span className="text-[#F85037]">*</span></Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" className={`mt-1.5 border-[#E6E6E6] ${errors.name ? "border-red-300" : ""}`} data-testid="support-name" />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label className="text-zinc-700">Email Address <span className="text-[#F85037]">*</span></Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className={`mt-1.5 border-[#E6E6E6] ${errors.email ? "border-red-300" : ""}`} data-testid="support-email" />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label className="text-zinc-700">I am a <span className="text-[#F85037]">*</span></Label>
                <Select value={form.user_type} onValueChange={(v) => set("user_type", v)}>
                  <SelectTrigger className={`mt-1.5 border-[#E6E6E6] ${errors.user_type ? "border-red-300" : ""}`} data-testid="support-type">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Athlete">Athlete</SelectItem>
                    <SelectItem value="Club / Academy">Club / Academy</SelectItem>
                    <SelectItem value="Scout">Scout</SelectItem>
                    <SelectItem value="Coach">Coach</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.user_type && <p className="text-xs text-red-500 mt-1">{errors.user_type}</p>}
              </div>
              <div>
                <Label className="text-zinc-700">Subject <span className="text-[#F85037]">*</span></Label>
                <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="What's this about?" className={`mt-1.5 border-[#E6E6E6] ${errors.subject ? "border-red-300" : ""}`} data-testid="support-subject" />
                {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
              </div>
              <div>
                <Label className="text-zinc-700">Message <span className="text-[#F85037]">*</span></Label>
                <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Describe your issue or question..." className={`mt-1.5 border-[#E6E6E6] min-h-[120px] ${errors.message ? "border-red-300" : ""}`} data-testid="support-message" />
                {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#F85037] hover:bg-[#e04530] text-white h-11 gap-2" data-testid="support-submit">
                <Send size={14} /> {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
            <p className="text-xs text-zinc-400 text-center mt-6">You can also reach us at <a href="mailto:info@sportsunity.gr" className="text-[#074572] hover:underline">info@sportsunity.gr</a></p>
          </>
        )}
      </main>
    </div>
  );
}
